import onetime from "@-/com.npmjs.onetime";
import type { NativeProcessStatsSnapshot } from "@-/dev.improve.haniverse.modules.process-stats";
import { BoundedBuffer } from "#bounded-buffer";
import type {
  DiagnosticsEvent,
  DiagnosticsLogEntry,
  DiagnosticsResourceSample,
  DiagnosticsSessionSnapshot,
} from "#diagnostics-types";

// Buffer budgets. At the default 2 s sampling interval 2400 samples is ~80 minutes; logs and
// events are bounded by bytes as well so a log storm evicts old lines instead of growing memory.
const MAX_LOG_ENTRIES = 4000;
const MAX_LOG_BYTES = 2_000_000;
const MAX_SAMPLES = 2400;
const MAX_SAMPLE_BYTES = 3_000_000;
const MAX_EVENTS = 1500;
const MAX_EVENT_BYTES = 1_000_000;

// UI subscribers are notified at most this often, so a chatty logger cannot re-render the
// Diagnostics settings page on every line.
const NOTIFY_COALESCE_MS = 500;

function randomSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function estimateSize(value: unknown): number {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? 0 : json.length;
  } catch {
    return 256;
  }
}

export type DiagnosticsSummary = {
  approxBytes: number;
  droppedLogs: number;
  eventCount: number;
  isRecording: boolean;
  latestSample: DiagnosticsResourceSample | null;
  logCount: number;
  recordingStartedAt: number | null;
  sampleCount: number;
};

type DiagnosticsStoreState = {
  currentRoute: string | null;
  events: BoundedBuffer<DiagnosticsEvent>;
  isRecording: boolean;
  lastProcessSnapshot: NativeProcessStatsSnapshot | null;
  listeners: Set<() => void>;
  logs: BoundedBuffer<DiagnosticsLogEntry>;
  notifyTimer: ReturnType<typeof setTimeout> | null;
  recordingStartedAt: number | null;
  samples: BoundedBuffer<DiagnosticsResourceSample>;
  sessionId: string;
  sessionStartedAt: number;
  summary: DiagnosticsSummary | null;
};

const getState = onetime((): DiagnosticsStoreState => ({
  currentRoute: null,
  events: new BoundedBuffer(MAX_EVENTS, MAX_EVENT_BYTES),
  isRecording: false,
  lastProcessSnapshot: null,
  listeners: new Set(),
  logs: new BoundedBuffer(MAX_LOG_ENTRIES, MAX_LOG_BYTES),
  notifyTimer: null,
  recordingStartedAt: null,
  samples: new BoundedBuffer(MAX_SAMPLES, MAX_SAMPLE_BYTES),
  sessionId: randomSessionId(),
  sessionStartedAt: Date.now(),
  summary: null,
}));

function invalidate(): void {
  const state = getState();
  state.summary = null;
  if (state.notifyTimer !== null || state.listeners.size === 0) {
    return;
  }
  state.notifyTimer = setTimeout(() => {
    state.notifyTimer = null;
    for (const listener of state.listeners) {
      try {
        listener();
      } catch {
        // A UI subscriber must never break recording.
      }
    }
  }, NOTIFY_COALESCE_MS);
}

export function recordDiagnosticsLog(entry: DiagnosticsLogEntry): void {
  const state = getState();
  if (!state.isRecording) {
    return;
  }
  state.logs.push(entry, estimateSize(entry));
  invalidate();
}

export function recordDiagnosticsSample(sample: DiagnosticsResourceSample): void {
  const state = getState();
  if (!state.isRecording) {
    return;
  }
  state.samples.push(sample, estimateSize(sample));
  invalidate();
}

export function recordDiagnosticsEvent(event: DiagnosticsEvent): void {
  const state = getState();
  if (!state.isRecording && event.kind !== "recorder") {
    return;
  }
  state.events.push(event, estimateSize(event));
  invalidate();
}

/** Remembers the current route (always) and records a navigation event (while recording). */
export function setDiagnosticsRoute(pathname: string): void {
  const state = getState();
  if (state.currentRoute === pathname) {
    return;
  }
  state.currentRoute = pathname;
  recordDiagnosticsEvent({ kind: "navigation", pathname, timestamp: Date.now() });
}

export function getDiagnosticsRoute(): string | null {
  return getState().currentRoute;
}

export function setDiagnosticsLastProcessSnapshot(
  snapshot: NativeProcessStatsSnapshot | null,
): void {
  getState().lastProcessSnapshot = snapshot;
}

export function getDiagnosticsLastProcessSnapshot(): NativeProcessStatsSnapshot | null {
  return getState().lastProcessSnapshot;
}

export function markDiagnosticsRecording(isRecording: boolean): void {
  const state = getState();
  if (state.isRecording === isRecording) {
    return;
  }
  state.isRecording = isRecording;
  state.recordingStartedAt = isRecording ? Date.now() : state.recordingStartedAt;
  recordDiagnosticsEvent({
    action: isRecording ? "started" : "stopped",
    kind: "recorder",
    timestamp: Date.now(),
  });
}

export function isDiagnosticsRecording(): boolean {
  return getState().isRecording;
}

export function getDiagnosticsSessionInfo(): { sessionId: string; startedAt: number } {
  const state = getState();
  return { sessionId: state.sessionId, startedAt: state.sessionStartedAt };
}

export function getDiagnosticsRecordingStartedAt(): number | null {
  return getState().recordingStartedAt;
}

/** Drops every buffered log, sample and event (the recorder keeps running if it was). */
export function clearDiagnostics(): void {
  const state = getState();
  state.logs.clear();
  state.samples.clear();
  state.events.clear();
  state.lastProcessSnapshot = null;
  if (state.isRecording) {
    state.recordingStartedAt = Date.now();
  }
  recordDiagnosticsEvent({ action: "cleared", kind: "recorder", timestamp: Date.now() });
  invalidate();
}

/**
 * Everything buffered so far, bounded to `limits` (newest entries win). Used for the report and
 * for the periodic on-disk session snapshot.
 */
export function snapshotDiagnostics(limits: {
  events: number;
  logs: number;
  samples: number;
}): DiagnosticsSessionSnapshot {
  const state = getState();
  return {
    events: state.events.tail(limits.events),
    logs: state.logs.tail(limits.logs),
    samples: state.samples.tail(limits.samples),
    savedAt: Date.now(),
    sessionId: state.sessionId,
    startedAt: state.sessionStartedAt,
  };
}

/** Referentially stable between changes, as `useSyncExternalStore` requires. */
export function getDiagnosticsSummary(): DiagnosticsSummary {
  const state = getState();
  if (state.summary !== null) {
    return state.summary;
  }
  const latest = state.samples.tail(1)[0] ?? null;
  state.summary = {
    approxBytes: state.logs.bytes + state.samples.bytes + state.events.bytes,
    droppedLogs: state.logs.dropped,
    eventCount: state.events.length,
    isRecording: state.isRecording,
    latestSample: latest,
    logCount: state.logs.length,
    recordingStartedAt: state.recordingStartedAt,
    sampleCount: state.samples.length,
  };
  return state.summary;
}

export function subscribeToDiagnostics(listener: () => void): () => void {
  const state = getState();
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}
